import React, { useCallback, useEffect, useState } from "react";
import { map, mergeRight } from "ramda";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import { FetchError, fetchSecurityUser } from "../util/fetchUtils";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import List from "@mui/material/List";
import makeStyles from "@mui/styles/makeStyles";
import KeystoreListItem from "./KeystoreListItem";
import {
  useApplicationAccountKeystore,
  useApplicationAccountKeystoreDelete,
} from "./applicationAccountServices";
import { isBlank, notBlank } from "../util/ext";
import CreateKeystoreDialog from "./CreateKeystoreDialog";
import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "white",
  },
}));

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const ApplicationAccountKeystore = ({ account, open, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const [currentKeyPair, setCurrentKeyPair] = useState({});
  const [errorAlert, setErrorAlert] = useState({ open: false });

  const [keystore, setKeystore] = useState([]);
  const results = useApplicationAccountKeystore(account);
  useEffect(() => {
    if (results.isError && results.error.status === 401) {
      dispatch(logOut());
    }
    const fetchUsers = async acct => {
      const createdByUser = await lookupUser(auth, acct.createdBy);

      return mergeRight(acct, {
        createdByUsername: createdByUser.username,
      });
    };

    if (results.isSuccess && notBlank(results.data)) {
      Promise.all(map(fetchUsers, results.data)).then(data => {
        setKeystore(data);
      });
    } else {
      setKeystore([]);
    }
  }, [
    dispatch,
    auth,
    results.data,
    results.isError,
    results.error,
    results.isSuccess,
  ]);

  const showAlert = useCallback(
    message =>
      setErrorAlert({
        open: true,
        message,
      }),
    [],
  );

  const handleError = useCallback(
    (message, err) => {
      if (err instanceof FetchError) {
        if (err.status === 401) {
          dispatch(logOut());
        } else {
          showAlert(message);
        }
      } else {
        showAlert(err.message);
      }
    },
    [dispatch, showAlert],
  );

  const handleRowSelected = useCallback(keypair => {
    setCurrentKeyPair(keypair);
  }, []);

  const deleteKeystore = useApplicationAccountKeystoreDelete();
  const handleDeleteClicked = useCallback(
    async keypair => {
      setCurrentKeyPair(keypair);

      try {
        await deleteKeystore.mutateAsync({
          resourceIds: {
            id: account.applicationId,
            accessKey: keypair.accessKey,
          },
        });
      } catch (err) {
        handleError("unable to delete Key Pair.", err);
      } finally {
        results.refetch();
      }
    },
    [account, deleteKeystore, results, handleError],
  );

  const handleNewKeystoreClicked = useCallback(() => {
    setCreateKeystoreDialogOpen(true);
  }, []);

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const [createKeystoreDialogOpen, setCreateKeystoreDialogOpen] = useState(
    false,
  );

  const handleCreateKeystoreSave = useCallback(() => {
    results.refetch();
    setCreateKeystoreDialogOpen(false);
  }, [results]);

  const handleCreateKeystoreClose = useCallback(() => {
    setCreateKeystoreDialogOpen(false);
  }, []);

  return (
    <Dialog
      maxWidth="md"
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        <Grid container justifyContent="space-between">
          <Grid>Application Account Key Pairs</Grid>
          <Grid>
            <Button
              variant={"text"}
              startIcon={<AddIcon />}
              onClick={handleNewKeystoreClicked}
            >
              New Key Pair
            </Button>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <CreateKeystoreDialog
          open={createKeystoreDialogOpen}
          account={account}
          onClose={handleCreateKeystoreClose}
          onSave={handleCreateKeystoreSave}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}></Grid>
          <Grid item xs={12}>
            {results.isLoading && <PanelLoadingIndicator />}
            {results.isSuccess && isBlank(keystore) && (
              <Box padding={5} fontSize="1.4rem" fontStyle="italic">
                No Keystore set up for this Organization.
              </Box>
            )}
            {results.isSuccess && notBlank(keystore) && (
              <List classes={{ root: classes.root }}>
                {map(keypair => {
                  const selected =
                    keypair.accessKey === currentKeyPair.accessKey;
                  return (
                    <KeystoreListItem
                      key={keypair.accessKey}
                      keystore={keypair}
                      selected={selected}
                      onDeleteClick={handleDeleteClicked}
                      onSelect={handleRowSelected}
                    />
                  );
                }, keystore)}
              </List>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Close
        </Button>
      </DialogActions>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ApplicationAccountKeystore;
